import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Truck, Container, Factory, Plus, Pencil, Trash2, Search, X, Phone, MapPin, IdCard, Gauge } from 'lucide-react';
import { PageShell, glassLight, primaryButton, ghostButton, inputStyle, EmptyState, Field, MotionGrid, MotionItem, CampoValidado, useConfirmacion, useIsMobile, T } from '../ui';
import type { Camion, Cola, Conductor, Envasadora } from '../types';

// Catálogos que alimentan el formulario de orden de pedido. Van juntos en un
// módulo porque se mantienen juntos: un conductor sin su camión y su cola
// asignados no sirve de nada, y una orden sin envasadoras no tiene a dónde ir.

type TabFlota = 'conductores' | 'camiones' | 'colas' | 'envasadoras';
const TABS: {
  id: TabFlota;
  label: string;
  icono: React.ElementType;
  color: string;
  soft: string;
}[] = [{
  id: 'conductores',
  label: 'Conductores',
  icono: User,
  color: '#FF6B01',
  soft: 'rgba(255,107,1,.12)'
}, {
  id: 'camiones',
  label: 'Camiones',
  icono: Truck,
  color: '#2563eb',
  soft: 'rgba(37,99,235,.12)'
}, {
  id: 'colas',
  label: 'Colas',
  icono: Container,
  color: '#7c3aed',
  soft: 'rgba(124,58,237,.12)'
}, {
  id: 'envasadoras',
  label: 'Envasadoras',
  icono: Factory,
  color: '#16a34a',
  soft: 'rgba(22,163,74,.12)'
}];
const fmtGal = (n: number) => new Intl.NumberFormat('es-DO').format(n || 0);
const isoLocal = (d: Date) => {
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};

// Un solo objeto de formulario para las cuatro pestañas: los campos que no
// aplican simplemente se ignoran al guardar. Tener cuatro estados separados
// solo repetiría el mismo abrir/cerrar/limpiar cuatro veces.
interface FormFlota {
  nombre: string;
  pase: string;
  cedula: string;
  telefono: string;
  camionId: string;
  colaId: string;
  galones: string;
  placa: string;
  descripcion: string;
  ficha: string;
  capacidad: string;
  rnc: string;
  direccion: string;
}
const FORM_VACIO: FormFlota = {
  nombre: '',
  pase: '',
  cedula: '',
  telefono: '',
  camionId: '',
  colaId: '',
  galones: '',
  placa: '',
  descripcion: '',
  ficha: '',
  capacidad: '',
  rnc: '',
  direccion: ''
};

// Fila de dato dentro de una tarjeta: ícono tenue a la izquierda, texto que
// se parte si no cabe. Sin ícono el dato queda flotando sin jerarquía.
function Dato({
  icono: Icono,
  children
}: {
  icono: React.ElementType;
  children: React.ReactNode;
}) {
  return <div style={{
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start'
  }} data-id="element-575" data-mp="2dwa55yhnv6ov">
      <Icono size={13} style={{
      color: T.textMuted,
      flexShrink: 0,
      marginTop: 2.5
    }} data-id="element-576" data-mp="2irfz9kl7vro1" />
      <span style={{
      lineHeight: 1.45,
      wordBreak: 'break-word',
      minWidth: 0
    }} data-id="element-577" data-mp="2nq91uhyyty9f">
        {children}
      </span>
    </div>;
}
interface FlotaProps {
  camiones: Camion[];
  colas: Cola[];
  envasadoras: Envasadora[];
  conductores: Conductor[];
  onAddCamion: (c: Camion) => void;
  onUpdateCamion: (c: Camion) => void;
  onDeleteCamion: (id: string) => void;
  onAddCola: (c: Cola) => void;
  onUpdateCola: (c: Cola) => void;
  onDeleteCola: (id: string) => void;
  onAddEnvasadora: (e: Envasadora) => void;
  onUpdateEnvasadora: (e: Envasadora) => void;
  onDeleteEnvasadora: (id: string) => void;
  onAddConductor: (c: Conductor) => void;
  onUpdateConductor: (c: Conductor) => void;
  onDeleteConductor: (id: string) => void;
  canEdit: boolean;
  canArchive: boolean;
  onLog?: (mensaje: string) => void;
}
export function Flota({
  camiones,
  colas,
  envasadoras,
  conductores,
  onAddCamion,
  onUpdateCamion,
  onDeleteCamion,
  onAddCola,
  onUpdateCola,
  onDeleteCola,
  onAddEnvasadora,
  onUpdateEnvasadora,
  onDeleteEnvasadora,
  onAddConductor,
  onUpdateConductor,
  onDeleteConductor,
  canEdit,
  canArchive,
  onLog
}: FlotaProps) {
  const isMobile = useIsMobile();
  const [dialogo, confirmar] = useConfirmacion();
  const [tab, setTab] = useState<TabFlota>('conductores');
  const [busqueda, setBusqueda] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormFlota>(FORM_VACIO);
  const [error, setError] = useState('');
  const conf = TABS.find(t => t.id === tab)!;
  const camionesActivos = camiones.filter(c => c.activo !== false);
  const colasActivas = colas.filter(c => c.activo !== false);
  const conteos: Record<TabFlota, number> = {
    conductores: conductores.length,
    camiones: camiones.length,
    colas: colas.length,
    envasadoras: envasadoras.length
  };
  const cambiarTab = (id: TabFlota) => {
    setTab(id);
    setBusqueda('');
    cerrarForm();
  };
  const cerrarForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(FORM_VACIO);
    setError('');
  };
  const abrirNuevo = () => {
    setForm(FORM_VACIO);
    setEditingId(null);
    setError('');
    setShowForm(true);
  };
  const set = (patch: Partial<FormFlota>) => {
    setForm(prev => ({
      ...prev,
      ...patch
    }));
    setError('');
  };

  // Elegir la cola propone su capacidad como galones del viaje: es el número
  // que se va a cargar salvo que el despachador decida otra cosa.
  const elegirCola = (colaId: string) => {
    const cola = colasActivas.find(c => c.id === colaId);
    setForm(prev => ({
      ...prev,
      colaId,
      galones: !prev.galones && cola?.capacidadGalones ? String(cola.capacidadGalones) : prev.galones
    }));
    setError('');
  };

  // ── LISTADO DE LA PESTAÑA ACTIVA ──────────────────────────────────────
  const items = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const coincide = (...campos: string[]) => !q || campos.some(c => (c ?? '').toLowerCase().includes(q));
    if (tab === 'conductores') {
      return conductores.filter(c => coincide(c.nombre, c.pase, c.cedula, c.placa, c.fichaCola)).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    }
    if (tab === 'camiones') {
      return camiones.filter(c => coincide(c.placa, c.descripcion)).sort((a, b) => a.placa.localeCompare(b.placa, 'es'));
    }
    if (tab === 'colas') {
      return colas.filter(c => coincide(c.ficha, c.descripcion)).sort((a, b) => a.ficha.localeCompare(b.ficha, 'es'));
    }
    return envasadoras.filter(e => coincide(e.nombre, e.rnc, e.direccion, e.telefono)).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [tab, busqueda, conductores, camiones, colas, envasadoras]);

  // ── EDITAR ────────────────────────────────────────────────────────────
  const editar = (id: string) => {
    setEditingId(id);
    setError('');
    setShowForm(true);
    if (tab === 'conductores') {
      const c = conductores.find(x => x.id === id);
      if (!c) return;
      setForm({
        ...FORM_VACIO,
        nombre: c.nombre,
        pase: c.pase,
        cedula: c.cedula,
        telefono: c.telefono,
        camionId: c.camionId,
        colaId: c.colaId,
        galones: c.galones ? String(c.galones) : ''
      });
    } else if (tab === 'camiones') {
      const c = camiones.find(x => x.id === id);
      if (!c) return;
      setForm({
        ...FORM_VACIO,
        placa: c.placa,
        descripcion: c.descripcion
      });
    } else if (tab === 'colas') {
      const c = colas.find(x => x.id === id);
      if (!c) return;
      setForm({
        ...FORM_VACIO,
        ficha: c.ficha,
        capacidad: c.capacidadGalones ? String(c.capacidadGalones) : '',
        descripcion: c.descripcion
      });
    } else {
      const e = envasadoras.find(x => x.id === id);
      if (!e) return;
      setForm({
        ...FORM_VACIO,
        nombre: e.nombre,
        rnc: e.rnc,
        direccion: e.direccion,
        telefono: e.telefono
      });
    }
  };

  // ── GUARDAR ───────────────────────────────────────────────────────────
  const guardar = async () => {
    const hoy = isoLocal(new Date());
    // Nombre/placa/ficha son la identidad del registro: sin ellos no hay nada
    // que elegir en el desplegable de la orden.
    const repetido = (lista: {
      id: string;
    }[], igual: (x: any) => boolean) => lista.some(x => igual(x) && x.id !== editingId);
    if (tab === 'conductores') {
      const nombre = form.nombre.trim();
      if (!nombre) return setError('Escribe el nombre del conductor.');
      if (repetido(conductores, (x: Conductor) => x.nombre.trim().toLowerCase() === nombre.toLowerCase())) {
        return setError('Ese conductor ya está registrado.');
      }
      const camion = camionesActivos.find(x => x.id === form.camionId);
      const cola = colasActivas.find(x => x.id === form.colaId);
      const base: Conductor = {
        id: editingId ?? Date.now().toString(),
        nombre,
        pase: form.pase.trim(),
        cedula: form.cedula.trim(),
        telefono: form.telefono.trim(),
        camionId: form.camionId,
        // La placa y la ficha se copian como texto para que la orden no pierda
        // el dato si más adelante se da de baja el camión o la cola.
        placa: camion?.placa ?? '',
        colaId: form.colaId,
        fichaCola: cola?.ficha ?? '',
        galones: Number(form.galones) || 0,
        activo: true,
        fecha_registro: editingId ? conductores.find(x => x.id === editingId)?.fecha_registro || hoy : hoy
      };
      const ok = await confirmar({
        titulo: editingId ? '¿Guardar los cambios?' : '¿Registrar este conductor?',
        mensaje: 'Aparecerá en el desplegable al armar una orden de pedido, con la unidad y los galones que le asignes.',
        detalles: [{
          etiqueta: 'Conductor',
          valor: base.nombre
        }, {
          etiqueta: 'Pase',
          valor: base.pase || '—'
        }, {
          etiqueta: 'Camión',
          valor: base.placa || 'Sin asignar'
        }, {
          etiqueta: 'Cola',
          valor: base.fichaCola || 'Sin asignar'
        }, {
          etiqueta: 'Galones',
          valor: base.galones ? fmtGal(base.galones) : '—'
        }],
        confirmar: editingId ? 'Guardar cambios' : 'Registrar',
        tono: 'accent'
      });
      if (!ok) return;
      if (editingId) {
        onUpdateConductor(base);
        onLog?.(`Conductor editado: ${base.nombre}`);
      } else {
        onAddConductor(base);
        onLog?.(`Conductor registrado: ${base.nombre}`);
      }
    } else if (tab === 'camiones') {
      const placa = form.placa.trim().toUpperCase();
      if (!placa) return setError('Escribe la placa del camión.');
      if (repetido(camiones, (x: Camion) => x.placa.trim().toUpperCase() === placa)) {
        return setError('Ese camión ya está registrado.');
      }
      const base: Camion = {
        id: editingId ?? Date.now().toString(),
        placa,
        descripcion: form.descripcion.trim(),
        activo: true,
        fecha_registro: editingId ? camiones.find(x => x.id === editingId)?.fecha_registro || hoy : hoy
      };
      const ok = await confirmar({
        titulo: editingId ? '¿Guardar los cambios?' : '¿Registrar este camión?',
        mensaje: 'Podrás asignárselo a un conductor para que su placa entre sola en la orden.',
        detalles: [{
          etiqueta: 'Placa',
          valor: base.placa
        }, {
          etiqueta: 'Descripción',
          valor: base.descripcion || '—'
        }],
        confirmar: editingId ? 'Guardar cambios' : 'Registrar',
        tono: 'accent'
      });
      if (!ok) return;
      if (editingId) {
        onUpdateCamion(base);
        onLog?.(`Camión editado: ${base.placa}`);
      } else {
        onAddCamion(base);
        onLog?.(`Camión registrado: ${base.placa}`);
      }
    } else if (tab === 'colas') {
      const ficha = form.ficha.trim().toUpperCase();
      if (!ficha) return setError('Escribe la ficha de la cola.');
      if (repetido(colas, (x: Cola) => x.ficha.trim().toUpperCase() === ficha)) {
        return setError('Esa cola ya está registrada.');
      }
      const base: Cola = {
        id: editingId ?? Date.now().toString(),
        ficha,
        capacidadGalones: Number(form.capacidad) || 0,
        descripcion: form.descripcion.trim(),
        activo: true,
        fecha_registro: editingId ? colas.find(x => x.id === editingId)?.fecha_registro || hoy : hoy
      };
      const ok = await confirmar({
        titulo: editingId ? '¿Guardar los cambios?' : '¿Registrar esta cola?',
        mensaje: 'Su capacidad se propone como los galones del viaje cuando se le asigna a un conductor.',
        detalles: [{
          etiqueta: 'Ficha',
          valor: base.ficha
        }, {
          etiqueta: 'Capacidad',
          valor: base.capacidadGalones ? `${fmtGal(base.capacidadGalones)} galones` : '—'
        }],
        confirmar: editingId ? 'Guardar cambios' : 'Registrar',
        tono: 'accent'
      });
      if (!ok) return;
      if (editingId) {
        onUpdateCola(base);
        onLog?.(`Cola editada: ${base.ficha}`);
      } else {
        onAddCola(base);
        onLog?.(`Cola registrada: ${base.ficha}`);
      }
    } else {
      const nombre = form.nombre.trim();
      if (!nombre) return setError('Escribe el nombre de la envasadora.');
      if (repetido(envasadoras, (x: Envasadora) => x.nombre.trim().toLowerCase() === nombre.toLowerCase())) {
        return setError('Esa envasadora ya está registrada.');
      }
      const base: Envasadora = {
        id: editingId ?? Date.now().toString(),
        nombre,
        rnc: form.rnc.trim(),
        direccion: form.direccion.trim(),
        telefono: form.telefono.trim(),
        activo: true,
        fecha_registro: editingId ? envasadoras.find(x => x.id === editingId)?.fecha_registro || hoy : hoy
      };
      const ok = await confirmar({
        titulo: editingId ? '¿Guardar los cambios?' : '¿Registrar esta envasadora?',
        mensaje: 'Es uno de los destinos que se pueden elegir al armar una orden. Su nombre y su RNC son los que se imprimen.',
        detalles: [{
          etiqueta: 'Envasadora',
          valor: base.nombre
        }, {
          etiqueta: 'RNC',
          valor: base.rnc || '—'
        }, {
          etiqueta: 'Dirección',
          valor: base.direccion || '—'
        }, {
          etiqueta: 'Teléfono',
          valor: base.telefono || '—'
        }],
        confirmar: editingId ? 'Guardar cambios' : 'Registrar',
        tono: 'accent'
      });
      if (!ok) return;
      if (editingId) {
        onUpdateEnvasadora(base);
        onLog?.(`Envasadora editada: ${base.nombre}`);
      } else {
        onAddEnvasadora(base);
        onLog?.(`Envasadora registrada: ${base.nombre}`);
      }
    }
    cerrarForm();
  };

  // ── ELIMINAR ──────────────────────────────────────────────────────────
  const eliminar = async (id: string, titulo: string, aviso: string, borrar: (id: string) => void) => {
    const ok = await confirmar({
      titulo: `¿Quitar ${titulo}?`,
      mensaje: aviso,
      confirmar: 'Sí, quitar',
      tono: 'warn'
    });
    if (!ok) return;
    borrar(id);
    onLog?.(`${conf.label.slice(0, -1)} eliminado del registro: ${titulo}`);
  };

  // ── TARJETAS ──────────────────────────────────────────────────────────
  const tarjeta = (x: any) => {
    if (tab === 'conductores') {
      const c = x as Conductor;
      return {
        id: c.id,
        titulo: c.nombre,
        etiqueta: c.pase || 'Sin pase',
        filas: [{
          icono: Truck,
          texto: c.placa || 'Sin camión asignado'
        }, {
          icono: Container,
          texto: c.fichaCola || 'Sin cola asignada'
        }, {
          icono: Gauge,
          texto: c.galones ? `${fmtGal(c.galones)} galones asignados` : 'Sin galones asignados'
        }, ...(c.cedula ? [{
          icono: IdCard,
          texto: c.cedula
        }] : []), ...(c.telefono ? [{
          icono: Phone,
          texto: c.telefono
        }] : [])],
        borrar: () => eliminar(c.id, c.nombre, 'Deja de aparecer en el desplegable de las órdenes. Las que ya lo tienen registrado no cambian.', onDeleteConductor)
      };
    }
    if (tab === 'camiones') {
      const c = x as Camion;
      const asignados = conductores.filter(k => k.camionId === c.id).length;
      return {
        id: c.id,
        titulo: c.placa,
        etiqueta: asignados ? `${asignados} conductor${asignados > 1 ? 'es' : ''}` : 'Sin asignar',
        filas: [{
          icono: Truck,
          texto: c.descripcion || 'Sin descripción'
        }],
        borrar: () => eliminar(c.id, c.placa, 'Se quita también de los conductores que lo tengan asignado.', onDeleteCamion)
      };
    }
    if (tab === 'colas') {
      const c = x as Cola;
      const asignados = conductores.filter(k => k.colaId === c.id).length;
      return {
        id: c.id,
        titulo: c.ficha,
        etiqueta: asignados ? `${asignados} conductor${asignados > 1 ? 'es' : ''}` : 'Sin asignar',
        filas: [{
          icono: Gauge,
          texto: c.capacidadGalones ? `${fmtGal(c.capacidadGalones)} galones de capacidad` : 'Capacidad sin registrar'
        }, ...(c.descripcion ? [{
          icono: Container,
          texto: c.descripcion
        }] : [])],
        borrar: () => eliminar(c.id, c.ficha, 'Se quita también de los conductores que la tengan asignada.', onDeleteCola)
      };
    }
    const e = x as Envasadora;
    return {
      id: e.id,
      titulo: e.nombre,
      etiqueta: e.rnc || 'Sin RNC',
      filas: [{
        icono: MapPin,
        texto: e.direccion || 'Sin dirección registrada'
      }, {
        icono: Phone,
        texto: e.telefono || 'Sin teléfono registrado'
      }],
      borrar: () => eliminar(e.id, e.nombre, 'Deja de aparecer como destino al armar una orden. Las órdenes ya registradas no cambian.', onDeleteEnvasadora)
    };
  };
  const Icono = conf.icono;
  return <>
      <PageShell title="Flota y Destinos" sub="Conductores, camiones, colas y envasadoras que alimentan las órdenes de pedido" action={canEdit ? <button onClick={showForm ? cerrarForm : abrirNuevo} style={primaryButton} data-id="element-579" data-mp="hd49vnxf4u4w">
              {showForm ? <><X size={15} data-id="element-580" data-mp="cdi23u0d063h" /> Cerrar</> : <><Plus size={15} data-id="element-581" data-mp="cet4pqi2j7t0" /> Nuevo registro</>}
            </button> : undefined} data-id="element-578" data-mp="3qv1ztig0bz2s">

        {/* ── PESTAÑAS ── */}
        <div style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginBottom: 20
      }} data-id="element-582" data-mp="7g807evsa1yr">

          {TABS.map(t => {
          const activa = tab === t.id;
          const IconoTab = t.icono;
          return <button key={t.id} type="button" onClick={() => cambiarTab(t.id)} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            borderRadius: 99,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
            color: activa ? t.color : T.textSecondary,
            background: activa ? t.soft : 'transparent',
            border: `1px solid ${activa ? `${t.color}44` : T.border}`,
            transition: 'all .15s'
          }} data-id="element-583" data-mp="105os8bfhrgug">

                <IconoTab size={15} data-id="element-584" data-mp="20b9a01huikqv" />
                {t.label}
                <span style={{
              fontSize: 11,
              fontWeight: 800,
              padding: '1px 7px',
              borderRadius: 99,
              background: activa ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
              color: activa ? t.color : T.textMuted
            }} data-id="element-585" data-mp="20dvgdxt02u7i">

                  {conteos[t.id]}
                </span>
              </button>;
        })}
        </div>

        {/* ── FORMULARIO ── */}
        <AnimatePresence data-id="element-586" data-mp="2o1d3hg1bl445">
          {showForm && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} style={{
          overflow: 'hidden',
          marginBottom: 22
        }} data-id="element-587" data-mp="2o0bncx5g5rfk">

              <div style={{
            ...glassLight,
            padding: isMobile ? 18 : 24
          }} data-id="element-588" data-mp="ec0f0cd6wfk">

                <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 18
            }} data-id="element-589" data-mp="5dopzsumehlu">

                  <div style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                flexShrink: 0,
                background: conf.soft,
                border: `1px solid ${conf.color}30`,
                color: conf.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }} data-id="element-590" data-mp="fajl01ropzvd">

                    <Icono size={16} data-id="element-591" data-mp="xzq4hr0ayvi8" />
                  </div>
                  <div style={{
                fontSize: 15,
                fontWeight: 800,
                color: T.text
              }} data-id="element-592" data-mp="1305k6p824f3d">

                    {editingId ? 'Editar' : 'Nuevo'}{' '}
                    {conf.label.slice(0, -1).toLowerCase()}
                  </div>
                </div>

                <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0,1fr))',
              gap: 16,
              marginBottom: 16
            }} data-id="element-593" data-mp="2ngc0m37e3c6d">

                  {tab === 'conductores' && <>
                      <Field label="Nombre completo *" data-id="element-594" data-mp="3b6zjfjiufm7p">
                        <CampoValidado modo="letras" valor={form.nombre} onChange={v => set({
                    nombre: v
                  })} placeholder="Nombre del conductor" maxLength={60} data-id="element-595" data-mp="3b6g5sf087jzh" />

                      </Field>
                      <Field label="Pase del chofer" data-id="element-596" data-mp="31etllt5wuakl">
                        <CampoValidado modo="codigo" valor={form.pase} onChange={v => set({
                    pase: v
                  })} placeholder="APP-87" maxLength={15} data-id="element-597" data-mp="2wha66pxh7rr2" />

                      </Field>
                      <Field label="Cédula" data-id="element-598" data-mp="3p561bq7edblq">
                        <CampoValidado modo="cedula" valor={form.cedula} onChange={v => set({
                    cedula: v
                  })} placeholder="001-0000000-0" data-id="element-599" data-mp="1n96zzks32drx" />

                      </Field>
                      <Field label="Teléfono" data-id="element-600" data-mp="1djnahw9v7lo3">
                        <CampoValidado modo="telefono" valor={form.telefono} onChange={v => set({
                    telefono: v
                  })} placeholder="(809) 000-0000" data-id="element-601" data-mp="1divrdkxgfgep" />

                      </Field>
                      <Field label="Camión asignado" data-id="element-602" data-mp="13jwjw5ofdqta">
                        <select style={inputStyle} value={form.camionId} onChange={e => set({
                    camionId: e.target.value
                  })} data-id="element-603" data-mp="18kkhja75t5wh">

                          <option value="" data-id="element-604" data-mp="zjmccokziscb">
                            {camionesActivos.length ? '— Sin asignar —' : '— No hay camiones registrados —'}
                          </option>
                          {camionesActivos.map(c => <option key={c.id} value={c.id} data-id="element-605" data-mp="2txdhbasdco1f">
                              {c.placa}{c.descripcion ? ` · ${c.descripcion}` : ''}
                            </option>)}
                        </select>
                      </Field>
                      <Field label="Cola asignada" data-id="element-606" data-mp="2z3shlt7msnyi">
                        <select style={inputStyle} value={form.colaId} onChange={e => elegirCola(e.target.value)} data-id="element-607" data-mp="3ctnie3wbo415">
                          <option value="" data-id="element-608" data-mp="382xcmpjzhz5x">
                            {colasActivas.length ? '— Sin asignar —' : '— No hay colas registradas —'}
                          </option>
                          {colasActivas.map(c => <option key={c.id} value={c.id} data-id="element-609" data-mp="3htryvtx8cyrd">
                              {c.ficha}{c.capacidadGalones ? ` · ${fmtGal(c.capacidadGalones)} gal` : ''}
                            </option>)}
                        </select>
                      </Field>
                      <Field label="Galones asignados" data-id="element-610" data-mp="21eqreogkv4r5">
                        <CampoValidado modo="numeros" valor={form.galones} onChange={v => set({
                    galones: v
                  })} placeholder="10000" maxLength={7} data-id="element-611" data-mp="21f8vu8de8pa5" />

                      </Field>
                    </>}

                  {tab === 'camiones' && <>
                      <Field label="Placa *" data-id="element-612" data-mp="23scgje4ch6gd">
                        <CampoValidado modo="placa" valor={form.placa} onChange={v => set({
                    placa: v
                  })} placeholder="L-317355" data-id="element-613" data-mp="23v8e31ca5t4a" />

                      </Field>
                      <Field label="Descripción" data-id="element-614" data-mp="2crpqs90alz25">
                        <input style={inputStyle} value={form.descripcion} onChange={e => set({
                    descripcion: e.target.value
                  })} placeholder="Marca, modelo o color" maxLength={60} data-id="element-615" data-mp="1g4ugkvaxf22b" />

                      </Field>
                    </>}

                  {tab === 'colas' && <>
                      <Field label="Ficha *" data-id="element-616" data-mp="1twsl0stp09s5">
                        <CampoValidado modo="codigo" valor={form.ficha} onChange={v => set({
                    ficha: v
                  })} placeholder="33-21" maxLength={15} data-id="element-617" data-mp="wg3lj5s7tjlx" />

                      </Field>
                      <Field label="Capacidad en galones" data-id="element-618" data-mp="tvxj52j9217h">
                        <CampoValidado modo="numeros" valor={form.capacidad} onChange={v => set({
                    capacidad: v
                  })} placeholder="10000" maxLength={7} data-id="element-619" data-mp="ttbaezkmhfno" />

                      </Field>
                      <Field label="Descripción" data-id="element-620" data-mp="fvmrhxa48il9">
                        <input style={inputStyle} value={form.descripcion} onChange={e => set({
                    descripcion: e.target.value
                  })} placeholder="Opcional" maxLength={60} data-id="element-621" data-mp="fuuuqj4s8ete" />

                      </Field>
                    </>}

                  {tab === 'envasadoras' && <>
                      <Field label="Nombre *" data-id="element-622" data-mp="179pojock5iu">
                        <input style={inputStyle} value={form.nombre} onChange={e => set({
                    nombre: e.target.value
                  })} placeholder="Nombre de la envasadora" maxLength={60} data-id="element-623" data-mp="3sdb8jeek03f1" />

                      </Field>
                      <Field label="RNC" data-id="element-624" data-mp="3jdo38hipedgp">
                        <CampoValidado modo="rnc" valor={form.rnc} onChange={v => set({
                    rnc: v
                  })} placeholder="1-31-01000-1" data-id="element-625" data-mp="39oqx7e4qvugm" />

                      </Field>
                      <Field label="Dirección" data-id="element-626" data-mp="3dnzswojci2et">
                        <input style={inputStyle} value={form.direccion} onChange={e => set({
                    direccion: e.target.value
                  })} placeholder="Calle, sector, ciudad" maxLength={120} data-id="element-627" data-mp="2zrl6i701m5gx" />

                      </Field>
                      <Field label="Teléfono" data-id="element-628" data-mp="3shl3zks7sg9r">
                        <CampoValidado modo="telefono" valor={form.telefono} onChange={v => set({
                    telefono: v
                  })} placeholder="(809) 000-0000" data-id="element-629" data-mp="17w3ao5xdkeo" />

                      </Field>
                    </>}
                </div>

                {error ? <div style={{
              marginBottom: 14,
              padding: '11px 14px',
              borderRadius: 12,
              background: T.dangerSoft,
              border: `1px solid ${T.danger}33`,
              color: T.danger,
              fontSize: 12.5
            }} data-id="element-630" data-mp="f8oyzc7yqljd">

                    {error}
                  </div> : null}

                <div style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap'
            }} data-id="element-631" data-mp="1zq7xuq5ba0a3">

                  <button type="button" onClick={guardar} style={primaryButton} data-id="element-632" data-mp="29gj44jcgwlk5">
                    {editingId ? 'Guardar cambios' : 'Registrar'}
                  </button>
                  <button type="button" onClick={cerrarForm} style={ghostButton} data-id="element-633" data-mp="1lr8adltjbrfi">
                    Cancelar
                  </button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* ── BUSCADOR ── */}
        <div style={{
        position: 'relative',
        marginBottom: 18
      }} data-id="element-634" data-mp="1psaas5z6u9j2">

          <Search size={15} style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          color: T.textMuted
        }} data-id="element-635" data-mp="366a7z96nfl2m" />

          <input style={{
          ...inputStyle,
          paddingLeft: 34
        }} placeholder={`Buscar en ${conf.label.toLowerCase()}`} value={busqueda} onChange={e => setBusqueda(e.target.value)} data-id="element-636" data-mp="3lad28fbp0ogt" />

        </div>

        {/* ── LISTADO ── */}
        {items.length === 0 ? <EmptyState title={busqueda ? `Ningún registro coincide` : `Sin ${conf.label.toLowerCase()} registrados`} hint={busqueda ? 'Prueba con otro texto.' : tab === 'envasadoras' ? 'Registra las plantas a las que despachas para poder elegirlas como destino.' : 'Registra el primero para poder elegirlo al armar una orden.'} icon={<Icono size={22} data-id="element-638" data-mp="hte3cz3levia" />} data-id="element-637" data-mp="3u8one1tsiu1v" /> : <MotionGrid style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))',
        gap: 14
      }} data-id="element-639" data-mp="hu5xs6lgen6z">

            {items.map((x: any) => {
          const t = tarjeta(x);
          return <MotionItem key={t.id} style={{
            ...glassLight,
            padding: 0,
            overflow: 'hidden'
          }} data-id="element-640" data-mp="2jmalt6zu10cf">

                  <div style={{
              padding: '18px 20px 16px'
            }} data-id="element-641" data-mp="2g2a7qn20l7l1">

                    <div style={{
                display: 'flex',
                gap: 13,
                alignItems: 'flex-start',
                marginBottom: 13
              }} data-id="element-642" data-mp="2bbtounffctra">

                      <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  flexShrink: 0,
                  background: conf.soft,
                  border: `1px solid ${conf.color}30`,
                  color: conf.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }} data-id="element-643" data-mp="26ae3svd8yn8w">

                        <Icono size={18} data-id="element-644" data-mp="2l2fzs4c74w3n" />
                      </div>
                      <div style={{
                  minWidth: 0,
                  flex: 1
                }} data-id="element-645" data-mp="1elku7ghco88a">

                        <div style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: T.text,
                    lineHeight: 1.3,
                    wordBreak: 'break-word'
                  }} data-id="element-646" data-mp="19lyg74tp6nyf">

                          {t.titulo}
                        </div>
                        <span style={{
                    display: 'inline-block',
                    marginTop: 6,
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    padding: '3px 9px',
                    borderRadius: 99,
                    color: conf.color,
                    background: conf.soft,
                    border: `1px solid ${conf.color}30`
                  }} data-id="element-647" data-mp="gnf2q0rnhlar">

                          {t.etiqueta}
                        </span>
                      </div>
                    </div>

                    <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                fontSize: 12.5,
                color: T.textSecondary
              }} data-id="element-648" data-mp="31fgxymigr9ee">

                      {t.filas.map((f, i) => <Dato key={i} icono={f.icono} data-id="element-649" data-mp="1gvlk94anm0eu" data-mpi={i}>
                          {f.texto}
                        </Dato>)}
                    </div>
                  </div>

                  {/* Los botones van en su propia fila: metidos junto al
                      título aplastan el nombre en cuanto la tarjeta se
                      estrecha. */}
                  {canEdit || canArchive ? <div style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              padding: '11px 20px',
              borderTop: `1px solid ${T.border}`,
              background: T.surface
            }} data-id="element-650" data-mp="y4ubo4yhqe1i">

                      {canEdit ? <button type="button" onClick={() => editar(t.id)} style={{
                ...ghostButton,
                padding: '7px 13px',
                fontSize: 12.5
              }} data-id="element-651" data-mp="o8hn1lhsyqdm">

                          <Pencil size={13} data-id="element-652" data-mp="2iicvq9hc2aqj" /> Editar
                        </button> : null}
                      {canArchive ? <button type="button" onClick={t.borrar} style={{
                ...ghostButton,
                padding: '7px 13px',
                fontSize: 12.5,
                color: T.warn,
                borderColor: `${T.warn}44`,
                background: T.warnSoft
              }} data-id="element-653" data-mp="3mij9sebhvwm1">

                          <Trash2 size={13} data-id="element-654" data-mp="e2hrp3qhr293" /> Quitar
                        </button> : null}
                    </div> : null}
                </MotionItem>;
        })}
          </MotionGrid>}
      </PageShell>
      {dialogo}
    </>;
}